<template>
  <Sidebar
    v-model:is-open="sidebarIsOpen"
    :has-changes="hasUnsavedChanges"
    @on-close="((sidebarIsOpen = false), $emit('closeAppointmentHandler'))"
  >
    <SidebarHeader
      icon="calendar-plus"
      :title="
        isNewAppointment
          ? $t('ui.crud.new', { item: $t('ui.entities.appointment.label') })
          : $t('ui.entities.appointment.reschedule')
      "
      :description="isNewAppointment ? 'Book a new appointment' : 'Update appointment'"
    />
    <SidebarContent>
      <form
        ref="form"
        @submit.prevent="onAppointmentSubmit"
      >
        <!-- Booked By -->
        <FormGroup
          v-if="authStore?.details.userType == 1"
          label="Booked By"
        >
          <div
            v-if="state.source === 'customer'"
            class="text-secondary"
          >
            Customer
          </div>
          <RadioTabbed
            v-else
            v-model="state.source"
            :options="[
              { value: 'retain', label: 'Retain' },
              { value: 'dealer', label: 'Dealer' }
            ]"
            full-width
            :disabled="authStore?.details.userType != 1 ? true : false"
            @option-change="setAvailableMethods"
          />
        </FormGroup>

        <!-- Linked Communication -->
        <FormGroup
          v-if="state.source !== 'dealer'"
          label="Source"
        >
          <!-- Select a communication type to link to the appointment -->
          <RadioTabbed
            v-model="state.communicationType"
            :options="[
              { value: 'call', label: 'Call', icon: 'phone' },
              { value: 'email', label: 'Email', icon: 'envelope' },
              { value: 'sms', label: 'SMS', icon: 'sms' }
            ]"
            full-width
            :has-error="v$.communicationType.$error"
          />
          <InputError :has-error="v$.communicationType.$error">
            {{ v$.communicationType.$errors[0]?.$message }}
          </InputError>

          <!-- TODO: Add select linked communication -->
          <!-- <pre>{{ latestCommunications }}</pre>
          <Select
            v-model="state.communicationId"
            :options="latestCommunications"
            value-key="id"
            @select="selectLinkedComm"
          >
            <template #value="{ value }">
              {{ value.type == 'sms' ? value.smsMessage : value.emailSubject }}
            </template>
            <template #option="{ option }">
              <div v-if="option.type == 'sms'">
                <div class="flex items-center gap-2">
                  <Icon type="sms" />
                  <div class="text-tertiary text-sm">{{ dayjs(option.createdAt).format('ll') }}</div>
                </div>
                <div class="ml-7">{{ _truncate(option.smsMessage, { length: 40 }) }}</div>
              </div>
              <div
                v-else-if="option.type == 'email'"
                class="w-[100%]"
              >
                <div class="flex items-center gap-2">
                  <Icon type="envelope" />
                  <div class="text-tertiary text-sm">{{ dayjs(option.createdAt).format('ll') }}</div>
                </div>
                <div class="ml-7">{{ _truncate(option.emailSubject, { length: 40 }) }}</div>
                <div class="flex">
                  <div class="ml-7 w-[100%] overflow-hidden text-sm text-secondary">
                    {{ _truncate(option.emailBody, { length: 50 }) }}
                  </div>
                </div>
              </div>
            </template>
          </Select> -->
        </FormGroup>

        <!-- Type -->
        <FormGroup label="Type">
          <Select
            v-model="state.type"
            :options="typeIntentOptions"
          />
        </FormGroup>
        <FormGroup label="Intent">
          <Select
            v-model="state.intent"
            :options="intentsComputed"
            :has-error="v$.intent.$error"
          />
          <InputError :has-error="v$.intent.$error">{{ v$.intent.$errors[0].$message }}</InputError>
        </FormGroup>
        <FormGroup label="Dealership">
          <Select
            v-model="state.dealershipID"
            :options="dealerships.data.value"
            label-key="displayName"
            value-key="id"
            :readonly="!isNewAppointment"
            searchable
            @select="changeDealership()"
          />
          <InputError :has-error="v$.dealershipID.$error">{{ v$.dealershipID.$errors[0].$message }}</InputError>
        </FormGroup>

        <FormGroup label="Preferred Staff">
          <Select
            v-model="state.requestedUserToAssign"
            :options="assignableUsers.data.value"
            :readonly="!isNewAppointment"
            value-key="id"
            searchable
            clearable
          >
            <template #value="{ value }">
              <div class="flex gap-2 w-full items-center">
                <Avatar
                  size="2xs"
                  :url="value?.profilePicUrl"
                  :text="value?.initials"
                />
                <div class="flex justify-between items-center w-full">
                  <span>{{ value?.fullName }}</span>
                  <span class="text-quaternary text-xs">{{ value?.jobTitle }}</span>
                </div>
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex gap-2 w-full items-center">
                <Avatar
                  size="2xs"
                  :url="option.profilePicUrl"
                  :text="option.initials"
                />
                <div class="flex justify-between items-center w-full">
                  <span>{{ option.fullName }}</span>
                  <span class="text-quaternary text-xs">{{ option.jobTitle }}</span>
                </div>
              </div>
            </template>
          </Select>
          <InputError :has-error="v$.assignedTo.$error">{{ v$.assignedTo.$errors[0].$message }}</InputError>
        </FormGroup>

        <Chip
          v-if="!$can('assign_appointments') && state.assignedTo"
          :avatar="state.assignedTo?.profilePicUrl"
          :avatar-text="state.assignedTo?.initials"
        >
          {{ state.assignedTo?.forename + ' ' + state.assignedTo?.surname }}
        </Chip>

        <FormGroup
          v-else
          :label="$t('ui.entities.task.assignedTo')"
          label-for="assignedTo"
        >
          <Select
            v-model="state.assignedTo"
            :options="assignableUsers.data.value"
            on-select-return-as-object
            value-key="id"
            searchable
            clearable
          >
            <template #value="{ value }">
              <div class="flex gap-2 w-full items-center">
                <Avatar
                  size="2xs"
                  :url="value?.profilePicUrl"
                  :text="value?.initials"
                />
                <div class="flex justify-between items-center w-full">
                  <span>{{ value?.fullName }}</span>
                  <span class="text-quaternary text-xs">{{ value?.jobTitle }}</span>
                </div>
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex gap-2 w-full items-center">
                <Avatar
                  size="2xs"
                  :url="option.profilePicUrl"
                  :text="option.initials"
                />
                <div class="flex justify-between items-center w-full">
                  <span>{{ option.fullName }}</span>
                  <span class="text-quaternary text-xs">{{ option.jobTitle }}</span>
                </div>
              </div>
            </template>
          </Select>
          <InputError :has-error="v$.assignedTo.$error">{{ v$.assignedTo.$errors[0].$message }}</InputError>
        </FormGroup>

        <FormGroup label="Method">
          <RadioBoxed
            v-model="state.method"
            :options="appMethodOptions"
          />
          <InputError :has-error="v$.method.$error">{{ v$.method.$errors[0].$message }}</InputError>
        </FormGroup>

        <FormGroup
          v-if="state.source == 'retain' && !bookAnyTime"
          :label="$t('ui.common.dateTime.label')"
          label-for="time"
        >
          <Dropdown
            v-tippy="!state.method ? 'Select a method first' : ''"
            class="w-full"
            :is-disabled="!state.method"
          >
            <template #triggerContent>
              <Input
                v-model="computedPrettyDateTime"
                icon="calendar-clock"
                :has-error="v$.time.$error"
                :readonly="!state.method"
              />
              <InputError :has-error="v$.time.$error">{{ v$.time.$errors[0].$message }}</InputError>
            </template>
            <div class="py-2 px-4">
              <AppointmentPicker
                :key="state.time"
                v-model="state.time"
                v-model:available-dates="filteredAvailability"
                :availability-mode="bookAnyTime ? 'any' : 'available'"
                @date-range-updated="onDateRangeUpdated"
                @on-confirm="onPickerConfirm()"
              />
            </div>
          </Dropdown>
          <div
            v-if="state.customerTimeZone != state.timeZone"
            class="bg-extra-light px-4 py-2 rounded mt-2"
          >
            <div class="text-sm text-warning">
              <Icon
                type="warning"
                class="mr-1"
              />
              <span>Customer is in a different time zone</span>
            </div>
            <div class="mt-1 text-sm text-secondary flex justify-between">
              <span v-tippy="'Customer'">
                <Icon type="user" />
                {{ state.customerTimeZone }}
              </span>
              <span v-if="state.time">{{ computedTimezones.timeAtCustomer }}</span>
            </div>
            <div class="text-sm text-secondary flex justify-between">
              <span v-tippy="'Dealership'">
                <Icon type="car-building" />
                {{ state.timeZone }}
              </span>
              <span v-if="state.time">{{ computedTimezones.timeAtDealership }}</span>
            </div>
          </div>
        </FormGroup>

        <FormGroup
          v-else
          :label="$t('ui.common.dateTime.label')"
          label-for="dateTime"
        >
          <DatePicker
            v-model="state.time"
            mode="single"
            display-as="input"
            display-format="full"
            show-time
            :has-error="v$.time.$error"
          />
          <InputError :has-error="v$.time.$error">{{ v$.time.$errors[0].$message }}</InputError>
        </FormGroup>

        <Checkbox
          v-if="state.source == 'retain'"
          v-model="bookAnyTime"
          class="mt-2 mb-4"
          :readonly="!$can('book_any_appointment')"
          label="Allow me to book any time and method"
        />

        <FormGroup label="Test Drive Interest">
          <Select
            v-model="state.testDriveVehicleModel"
            :options="testDriveVehicleModelOptions"
            label-key="label"
            value-key="value"
            searchable
            clearable
            :readonly="!state.testDriveVehicleMake"
            :placeholder="state.testDriveVehicleMake ? 'Select a model' : 'Vehicle make required'"
          />
        </FormGroup>

        <FormGroup
          :label="$t('ui.common.note')"
          label-for="note"
        >
          <TextArea
            id="note"
            v-model="state.note"
            :value="state.note"
            :has-error="v$.note.$error"
          ></TextArea>
          <InputError :has-error="v$.note.$error">{{ v$.note.$errors[0].$message }}</InputError>
        </FormGroup>

        <FormGroup
          :label="$t('ui.entities.task.createdBy')"
          label-for="createdBy"
        >
          <Chip
            v-if="!$can('set_appointment_creator')"
            :avatar="state.createdBy.profilePicUrl"
            :avatar-text="state.createdBy.initials"
          >
            {{ state.createdBy.forename + ' ' + state.createdBy.surname }}
          </Chip>

          <Select
            v-else
            v-model="state.createdBy"
            :options="users.data.value"
            on-select-return-as-object
            value-key="id"
            searchable
          >
            <template #value="{ value }">
              <div class="flex gap-2 w-full items-center">
                <Avatar
                  size="2xs"
                  :url="value?.profilePicUrl"
                  :text="value?.initials"
                />
                <div class="flex justify-between items-center w-full">
                  <span>{{ value?.fullName }}</span>
                  <span class="text-quaternary text-xs">{{ value?.jobTitle }}</span>
                </div>
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex gap-2 w-full items-center">
                <Avatar
                  size="2xs"
                  :url="option.profilePicUrl"
                  :text="option.initials"
                />
                <div class="flex justify-between items-center w-full">
                  <span>{{ option.fullName }}</span>
                  <span class="text-quaternary text-xs">{{ option.jobTitle }}</span>
                </div>
              </div>
            </template>
          </Select>

          <InputError :has-error="v$.createdBy.$error">{{ v$.createdBy.$errors[0].$message }}</InputError>
        </FormGroup>

        <!-- Notify Customer -->
        <FormGroup>
          <Checkbox
            v-model="state.notifyCustomer"
            :readonly="!canNotifyCustomer || !canSendAutomatedComms"
            label="Send notification to customer"
          ></Checkbox>
        </FormGroup>

        <FormGroup class="mb-4">
          <Checkbox
            v-model="state.notifyDealership"
            :readonly="!canSendAutomatedComms"
            label="Send notification to dealership"
          ></Checkbox>
        </FormGroup>

        <div
          v-if="!canSendAutomatedComms"
          class="text-warning text-sm mb-2"
        >
          <div v-if="!callCenterStore.selectedCallCenter">
            <Icon type="triangle-exclamation" />
            No call centre selected
          </div>
          <div v-else>
            <Icon type="triangle-exclamation" />
            Automated emails cannot be sent from this dealership
          </div>
        </div>
        <div
          v-if="!isCustomerEmailValid"
          class="text-warning text-sm mb-2"
        >
          <Icon type="triangle-exclamation" />
          Customer email is
          {{ agreementContactDetails?.email ? 'invalid' : 'missing' }}
        </div>
        <div
          v-if="!isCustomerNumberValid"
          class="text-warning text-sm"
        >
          <Icon type="triangle-exclamation" />
          Customer phone number is
          {{ agreementContactDetails?.mobile ? 'invalid' : 'missing' }}
        </div>
      </form>
    </SidebarContent>
    <SidebarFooter>
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('ui.actions.cancel')"
          severity="secondary"
          outlined
          type="button"
          :disabled="appointmentRequest.isLoading.value"
          @click="closeSidebar"
        />
        <Button
          :label="isNewAppointment ? $t('ui.actions.create') : $t('ui.actions.update')"
          :is-loading="appointmentRequest.isLoading.value"
          @click="onAppointmentSubmit"
        />
      </div>
    </SidebarFooter>
  </Sidebar>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import AppointmentPicker from '@/components/appointment-picker/AppointmentPicker.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'

import Icon from '@/components/icon/Icon.vue'
import Sidebar from '@/components/sidebar/Sidebar.vue'
import SidebarHeader from '@/components/sidebar/SidebarHeader.vue'
import SidebarContent from '@/components/sidebar/SidebarContent.vue'
import SidebarFooter from '@/components/sidebar/SidebarFooter.vue'

import FormGroup from '@/components/forms/FormGroup.vue'
import TextArea from '@/components/forms/TextArea.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Button from '@/components/button/Button.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Chip from '@/components/chip/Chip.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Select from '@/components/forms/Select.vue'

import Swal from 'sweetalert2'
import parsePhoneNumber from 'libphonenumber-js'

import { alertToast } from '@/utilities/notification'

import dayjs from 'dayjs'
import RadioBoxed from '@/components/forms/RadioBoxed.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  appointment: {
    type: Object,
    default: () => ({})
  },
  agreementContactDetails: {
    type: Object,
    default: null
  },
  dealershipManufacturerName: {
    type: String,
    default: null
  },
  agreementVehicleMake: {
    type: String,
    default: null
  },
  dealershipAutomatedCommsConfig: {
    type: Object,
    default: null
  }
})
const emit = defineEmits([
  'updateAppointmentSubmit',
  'closeAppointmentHandler',
  'newAppointmentSubmit',
  'appointmentUpdated'
])
const sidebarIsOpen = ref(props.isOpen)
const authStore = useAuthStore()
const callCenterStore = useCallCenterStore()

// Sync sidebarIsOpen with props.isOpen
watch(
  () => props.isOpen,
  newValue => {
    sidebarIsOpen.value = newValue
  }
)

const isNewAppointment = ref(true)

const bookAnyTime = ref(false)

const availableDatesArray = ref([])
const getAvailabilityApiRequest = useApiRequest()
const manufacturers = useApiRequest()
const vehicleModels = useApiRequest()

const isCustomerNumberValid = ref(false)
const isCustomerEmailValid = ref(false)
const canNotifyCustomer = ref(false)
const canSendAutomatedComms = ref(false)

function enableAutomatedComms() {
  if (
    props.agreementContactDetails?.mobile &&
    parsePhoneNumber(props.agreementContactDetails.mobile)?.isValid() &&
    parsePhoneNumber(props.agreementContactDetails.mobile)?.getType() == 'MOBILE'
  ) {
    isCustomerNumberValid.value = true
  }

  const emailValidationRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (props.agreementContactDetails?.email && emailValidationRegex.test(props.agreementContactDetails?.email)) {
    isCustomerEmailValid.value = true
  }

  if (props.dealershipAutomatedCommsConfig?.isPrimary && callCenterStore.selectedCallCenter) {
    canSendAutomatedComms.value = true
  }

  if (isCustomerNumberValid.value || isCustomerEmailValid.value) {
    canNotifyCustomer.value = true
  }
}
enableAutomatedComms()

// Default state of the form
const defaultState = {
  id: null,
  agreementID: null,
  type: 0,
  intent: null,
  source: authStore.details.userType == 1 ? 'retain' : 'dealer',
  method: null,
  time: null,
  timeZone: null,
  timeUtc: null,
  customerTimeZone: null,
  note: null,
  testDriveVehicleMake: null,
  testDriveVehicleModel: null,
  dealershipID: null,
  assignedTo: {
    id: null,
    forename: null,
    surname: null,
    profilePicUrl: null
  },
  createdByUserType: authStore.details.userType,
  createdBy: {
    id: authStore.details.id,
    forename: authStore.details.forename,
    surname: authStore.details.surname,
    profilePicUrl: authStore.details.profilePicUrl
  },
  notifyCustomer: canNotifyCustomer.value && canSendAutomatedComms.value,
  notifyDealership: canSendAutomatedComms.value,
  communicationType: null,
  communicationId: null
}

// State of the form
const state = ref({ ...defaultState })

// Users List
const users = useApiRequest()

const hasUnsavedChanges = ref(false)

/**
 * Falls back to the dealership manufacturer when the appointment payload does not
 * include a make yet. Brandless sites (multi-brand / used supermarkets) have no
 * manufacturer, so the agreement vehicle's make is used instead.
 */
function applyTestDriveVehicleMakeFallback() {
  if (!state.value.testDriveVehicleMake) {
    state.value.testDriveVehicleMake = props.dealershipManufacturerName ?? props.agreementVehicleMake ?? null
  }
}

function watchForChanges() {
  watch(
    state,
    () => {
      hasUnsavedChanges.value = true
    },
    { deep: true } // Deep watch to handle nested properties inside the state
  )
}

if (props.appointment) {
  if (props.appointment.id) {
    isNewAppointment.value = false
  }

  // Iterate over the properties of props.appointment and set them to the state reference
  Object.keys(props.appointment).forEach(key => {
    if (props.appointment[key] !== undefined) {
      state.value[key] = props.appointment[key]
    }
  })

  applyTestDriveVehicleMakeFallback()
  watchForChanges()
}

applyTestDriveVehicleMakeFallback()

// Assignable Users List
const assignmentMultiselect = ref()
const assignableUsers = useApiRequest()
// Track if data has been loaded to avoid duplicate requests
const dataLoaded = ref(false)

function fetchAssignableUsers() {
  if (!state.value.dealershipID) {
    return
  }
  try {
    assignableUsers
      .send({
        endpoint:
          '/v1/users?showDisabled=true&userType=externalUsers&forAppointmentAssignment=true&dealerships=' +
          state.value.dealershipID,
        method: 'GET'
      })
      .then(() => {
        if (assignableUsers.data.value && assignableUsers.data.value.length) {
          assignableUsers.data.value = assignableUsers.data.value.filter(user => {
            // Update 'disabled' property
            user.disabled = !user.isActive

            // Filter out disabled users, unless it's the one assigned to
            return !user.disabled || user.id === state.value.assignedTo?.id
          })
        }

        if (state.value.assignedTo != null && state.value.assignedTo > 0) {
          const isWithinAssignableUsers = assignableUsers.data.value.find(user => user.id === state.value.assignedTo.id)

          if (!isWithinAssignableUsers) {
            assignableUsers.data.value.push({
              id: state.value.assignedTo.id,
              fullName: state.value.assignedTo.forename + ' ' + state.value.assignedTo.surname,
              disabled: true
            })
          }

          const assignedUser = assignableUsers.data.value.find(obj => obj.id == state.value.assignedTo.id)
          assignmentMultiselect.value?.select(assignedUser)
        }
      })
  } catch (err) {
    alertToast('Failed to fetch users', err.message, 'error')
  }
}

function changeDealership() {
  state.value.assignedTo = null
  state.value.method = null
  state.value.time = null
  fetchAssignableUsers()
}

/**
 * Fetches manufacturers so the appointment form can resolve the dealership manufacturer.
 */
function fetchManufacturers() {
  manufacturers
    .send({
      endpoint: '/v1/manufacturers?showDisabled=true&showHidden=true',
      method: 'GET'
    })
    .then(response => {
      if (!response.success) {
        alertToast('Failed to fetch manufacturers', response.message, 'error')
        return
      }

      if (state.value.testDriveVehicleMake) {
        fetchTestDriveVehicleModels()
      }
    })
    .catch(err => {
      alertToast('Failed to fetch manufacturers', err.message, 'error')
    })
}

/**
 * Fetches test drive vehicle models for the derived dealership manufacturer.
 */
function fetchTestDriveVehicleModels() {
  if (!state.value.testDriveVehicleMake) {
    vehicleModels.data.value = []
    return
  }

  const manufacturer = manufacturers.data.value?.find(item => {
    return item.name?.trim().toLowerCase() === state.value.testDriveVehicleMake?.trim().toLowerCase()
  })

  if (!manufacturer?.id) {
    vehicleModels.data.value = []
    return
  }

  vehicleModels
    .send({
      endpoint: '/v1/vehicles/models',
      method: 'GET',
      params: {
        manufacturerId: manufacturer.id
      }
    })
    .then(response => {
      if (!response.success) {
        alertToast('Failed to fetch vehicle models', response.message, 'error')
      }
    })
    .catch(err => {
      vehicleModels.data.value = []
      alertToast('Failed to fetch vehicle models', err.message, 'error')
    })
}

// Get dealerships
const dealerships = useApiRequest()
const viewAllowed = authStore.accessAllowed
const dealershipsAccessible = viewAllowed.dealerships.map(dealership => dealership.id)

const testDriveVehicleModelOptions = computed(() => {
  const options = []
  const seenModels = new Set()

  if (Array.isArray(vehicleModels.data.value)) {
    vehicleModels.data.value.forEach(modelOption => {
      const modelName = modelOption?.model?.trim()

      if (!modelName) {
        return
      }

      const normalizedModelName = modelName.toLowerCase()

      if (seenModels.has(normalizedModelName)) {
        return
      }

      seenModels.add(normalizedModelName)
      options.push({
        label: modelName,
        value: modelName
      })
    })
  }

  if (state.value.testDriveVehicleModel) {
    const currentModel = state.value.testDriveVehicleModel.trim()
    const normalizedCurrentModel = currentModel.toLowerCase()

    if (!seenModels.has(normalizedCurrentModel)) {
      options.push({
        label: currentModel,
        value: currentModel
      })
    }
  }

  return options.sort((a, b) => a.label.localeCompare(b.label))
})

/**
 * Loads initial data when sidebar opens
 */
function loadInitialData() {
  if (dataLoaded.value) {
    return
  }
  dataLoaded.value = true

  // Fetch users list
  users.send({ endpoint: '/v1/users?userType=all' })

  // Fetch dealerships
  dealerships.send({ endpoint: '/v1/dealerships?ids=' + dealershipsAccessible, method: 'GET' })

  // Fetch manufacturers so the model field can be filtered by the dealership manufacturer
  fetchManufacturers()

  // Fetch assignable users if dealershipID is available
  if (state.value.dealershipID) {
    fetchAssignableUsers()
  }
}

// Watch for sidebar opening to load data
watch(
  () => sidebarIsOpen.value,
  isOpen => {
    if (isOpen) {
      loadInitialData()
    }
  },
  { immediate: true }
)

function getAvailability(start, end) {
  // console.log('Getting Availability for: ' + start + ' to ' + end)

  return new Promise((resolve, reject) => {
    getAvailabilityApiRequest
      .send({
        endpoint: '/v1/availability',
        method: 'GET',
        params: {
          dealershipId: state.value.dealershipID,
          startDate: start,
          endDate: end,
          ignoreAppointments: state.value.id
        }
      })
      .then(response => {
        availableDatesArray.value = response.data
        resolve(response.data)
      })
      .catch(err => {
        console.error('Error in getAvailability:', err)
        reject(err)
      })
  })
}

function onDateRangeUpdated({ start, end }) {
  // Fetch data for the new date range
  getAvailability(start, end)
}

// Watch for changes in state.source
watch(
  () => state.value.source,
  newSource => {
    // Reset the time and method if source is changed
    state.value.time = null
    state.value.method = null
    // Reset communicationType and communicationId to null if source is 'dealer'
    if (newSource === 'dealer') {
      state.value.communicationType = null
      state.value.communicationId = null
    }
  }
)

// Watch for changes in state.method
watch(
  () => state.value.method,
  () => {
    // Reset the time if method is changed
    state.value.time = null
  }
)

watch(
  () => state.value.testDriveVehicleMake,
  (newMake, oldMake) => {
    if (!newMake) {
      state.value.testDriveVehicleModel = null
      vehicleModels.data.value = []
      return
    }

    if (oldMake && newMake !== oldMake) {
      state.value.testDriveVehicleModel = null
    }

    if (manufacturers.data.value?.length) {
      fetchTestDriveVehicleModels()
    }
  }
)

const appMethodOptions = computed(() => {
  let options = [
    {
      value: 'physical',
      label: 'Physical',
      icon: 'location-dot',
      description: 'At dealership',
      disabled: false
    },
    {
      value: 'video',
      label: 'Video',
      icon: 'video',
      description: 'Dealer will host',
      disabled: false
    },
    {
      value: 'phone',
      label: 'Phone',
      icon: 'phone',
      description: 'Dealer will call',
      disabled: false
    }
  ]

  if (state.value.source == 'retain') {
    // If bookAnyTime is enabled, allow all methods
    if (bookAnyTime.value) {
      return options.map(option => ({ ...option, disabled: false }))
    }

    // For each method, check if any of the dates contain slots which allow that method, if not, default to disabled
    options = options.map(option => {
      const methodAllowed = availableDatesArray.value.some(date => {
        return date.slots.some(slot => {
          return slot.methodsAllowed[option.value]
        })
      })

      return { ...option, disabled: !methodAllowed }
    })
  }

  return options
})

// Computed function to filter out availability based on the method selected
const filteredAvailability = computed(() => {
  const method = state.value.method

  // return a version of availableDatesArray that is filtered based on the method selected.
  if (!availableDatesArray.value.length) {
    return []
  }

  const availableDatesFiltered = availableDatesArray.value.reduce((filteredDates, day) => {
    // Check if the day's methods include the desired method
    if (day.availableMethods[method]) {
      // Filter the slots directly and add the day to the result if it has valid slots
      const filteredSlots = day.slots.filter(slot => slot.methodsAllowed[method])
      if (filteredSlots.length > 0) {
        filteredDates.push({ ...day, slots: filteredSlots })
      }
    }
    return filteredDates
  }, [])

  return availableDatesFiltered
})

// Get availability for this month
if (props.isOpen) {
  getAvailability(dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD'))
}

function onPickerConfirm() {}

const vuelidateRules = computed(() => ({
  agreementID: { required },
  intent: { required },
  method: { required },
  dealershipID: { required },
  assignedTo: {},
  time: { required },
  note: { required },
  createdBy: { required },
  communicationType: state.value.source === 'dealer' ? {} : { required }
}))
const v$ = useVuelidate(vuelidateRules, state, { $stopPropagation: true })

function closeSidebar() {
  // check that no unsaved changes
  if (hasUnsavedChanges.value) {
    Swal.fire({
      title: 'Unsaved Changes',
      text: 'Changes made will not be saved if you continue',
      icon: 'warning',
      reverseButtons: true,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Discard',
      confirmButtonColor: 'var(--bg-error-solid)',
      cancelButtonText: 'Cancel'
    }).then(function (response) {
      if (response.isConfirmed) {
        sidebarIsOpen.value = false
        emit('closeAppointmentHandler')
      }
    })
  } else {
    sidebarIsOpen.value = false
    emit('closeAppointmentHandler')
  }
}

const appointmentRequest = useApiRequest()

async function onAppointmentSubmit() {
  const isFormCorrect = await v$.value.$validate()
  if (!isFormCorrect) {
    return
  }

  if (!state.value.notifyCustomer) {
    Swal.fire({
      title: 'Warning',
      text: `The customer won't be notified of the ${isNewAppointment.value ? 'new appointment' : 'appointment update'}`,
      icon: 'warning',
      reverseButtons: true,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Continue',
      confirmButtonColor: 'var(--bg-error-solid)',
      cancelButtonText: 'Cancel'
    }).then(function (response) {
      if (response.isConfirmed) {
        appointmentHandlerSubmit()
      }
    })
  } else {
    appointmentHandlerSubmit()
  }
}
function appointmentHandlerSubmit() {
  const testDriveVehicleModel = state.value.testDriveVehicleModel || null
  const testDriveVehicleMake = testDriveVehicleModel ? state.value.testDriveVehicleMake : null

  const jsonData = JSON.stringify({
    agreementId: state.value.agreementID,
    source: state.value.source,
    type: state.value.type,
    intent: state.value.intent,
    method: state.value.method,
    time: state.value.time,
    timeZone: state.value.timeZone,
    timeUtc: dayjs(dayjs.tz(state.value.time, state.value.timeZone)).utc().format('YYYY-MM-DD HH:mm:ss'),
    note: state.value.note,
    testDriveVehicleMake: testDriveVehicleMake,
    testDriveVehicleModel: testDriveVehicleModel,
    dealershipId: state.value.dealershipID,
    requestedUserToAssign: state.value.requestedUserToAssign,
    assignedTo: state.value.assignedTo?.id,
    createdByType: state.value.createdByUserType == 1 ? 'retain' : 'dealer',
    createdBy: state.value.createdBy?.id,
    notifyCustomer: state.value.notifyCustomer,
    notifyDealership: state.value.notifyDealership,
    communicationType: state.value.communicationType,
    communicationId: state.value.communicationId,
    updateSource: authStore.details.userType == 1 ? 'retain' : 'dealer',
    updatedByUserID: authStore.details.id
  })

  if (isNewAppointment.value) {
    appointmentRequest.send({ endpoint: '/v1/appointments', method: 'POST', data: jsonData }).then(response => {
      if (!response.success) {
        alertToast('Failed to create appointment', response.data.message, 'error')
      }
      // Check that the status from the API was successful
      if (response.success) {
        const newAppointmentID = response.data.id
        // Make sure the Appointment ID is received in the response
        if (!newAppointmentID) {
          alertToast('Warning', 'Appointment might not have been added', 'warning')
        } else {
          alertToast('Appointment Booked!', response.data.message, 'success')

          state.value.id = newAppointmentID
          emit('newAppointmentSubmit', state.value)
          hasUnsavedChanges.value = false
          closeSidebar()
        }
      }
    })
  } else {
    // Update existing appointment
    appointmentRequest
      .send({
        endpoint: '/v1/appointments/' + state.value.id + '/reschedule',
        method: 'PATCH',
        data: jsonData
      })
      .then(response => {
        if (response.success) {
          alertToast('Appointment Updated!', response.data.message, 'success')

          // Emit event to todo page with json, which will then add the appointment to the DOM
          emit('appointmentUpdated', state.value)
          hasUnsavedChanges.value = false
          closeSidebar()
        } else {
          alertToast('Failed to update appointment', response.data.message, 'error')
        }
      })
  }
}

const typeIntentOptions = [
  {
    value: 0,
    label: 'Finance Review',
    intents: [
      { value: 'Unsure', label: 'Unsure' },
      { value: 'Part-Exchange', label: 'Part-Exchange' },
      { value: 'Refinance', label: 'Refinance' },
      { value: 'Settle', label: 'Settle' },
      { value: 'Hand Back', label: 'Hand Back' },
      { value: 'Buy-Back', label: 'Buy-Back' }
    ]
  }
]

const intentsComputed = computed(() => {
  let intentOptions = []

  typeIntentOptions.forEach(type => {
    if (type.value === state.value.type) {
      intentOptions = type.intents
    }
  })

  return intentOptions
})

watch(
  () => state.value.type,
  () => {
    state.value.intent = null
  }
)

const computedTimezones = computed(() => {
  if (!state.value.time || !state.value.timeZone || !state.value.customerTimeZone)
    return {
      timeAtDealership: null,
      timeAtCustomer: null
    }

  const timeAtDealership = dayjs.tz(state.value.time, state.value.timeZone)
  const timeAtCustomer = dayjs.tz(timeAtDealership, state.value.customerTimeZone)
  return {
    timeAtDealership: timeAtDealership.format('lll'),
    timeAtCustomer: timeAtCustomer.format('lll')
  }
})

const computedPrettyDateTime = computed(() => {
  if (state.value.time) {
    return dayjs(state.value.time).format('lll')
  }
  return ''
})
</script>
